type CreateCSVAlgorithmDuration = {
  algorithm: string;
  durationMs: number;
  durationText: string;
};

type CreateCSVRun = {
  runAt: Date;
  runAtText: string;
  algorithmDurations: CreateCSVAlgorithmDuration[];
  errorLines: string[];
  rawLines: string[];
};

type CreateCSVBucketedRun = {
  bucketAt?: Date;
  bucketLabel: string;
  algorithmDurations: CreateCSVAlgorithmDuration[];
};

export type CreateCSVLogAnalysisResult = {
  runs: CreateCSVRun[];
  bucketedRuns: CreateCSVBucketedRun[];
  totalErrors: number;
  totalAlgorithms: number;
};

const parseDurationText = (text: string): number => {
  // "D day[s][,] H:MM:SS[.ffffff]" — Python timedelta or pandas Timedelta with days component
  // pandas: "0 days 00:00:15" (no comma, zero-padded hours)
  // Python: "1 day, 0:00:11" (comma, no leading zero on hours)
  const withDays = text.match(/^(\d+) days?,?\s*(\d+):(\d{2}):(\d{2})/);
  if (withDays) {
    const [, days, hours, minutes, seconds] = withDays;
    return (
      parseInt(days, 10) * 86400000 +
      parseInt(hours, 10) * 3600000 +
      parseInt(minutes, 10) * 60000 +
      parseInt(seconds, 10) * 1000
    );
  }

  // "H:MM:SS[.ffffff]" — Python timedelta without days (most common for short runtimes)
  const withoutDays = text.match(/^(\d+):(\d{2}):(\d{2})/);
  if (withoutDays) {
    const [, hours, minutes, seconds] = withoutDays;
    return (
      parseInt(hours, 10) * 3600000 +
      parseInt(minutes, 10) * 60000 +
      parseInt(seconds, 10) * 1000
    );
  }

  return 0;
};

const parseRunAt = (text: string): Date => {
  const datePart = text.trim();

  const parsed = new Date(datePart);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  // Normalize Python datetime format: replace space separator and truncate microseconds to milliseconds
  const isoStr = datePart
    .replace(" ", "T")
    .replace(/(\.\d{3})\d+/, "$1");
  const isoParsed = new Date(isoStr);
  if (!Number.isNaN(isoParsed.getTime())) {
    return isoParsed;
  }

  // "Weekday Mon DD HH:MM:SS AM/PM TZ YYYY" — JavaScript's Date.toString() format or Python strftime
  // JavaScript Date constructor rejects timezone abbreviations like CEST/CET, so parse manually
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dateStrMatch = datePart.match(
    /^\w{3}\s+(\w{3})\s+(\d{1,2})\s+(\d{2}):(\d{2}):(\d{2})\s+(AM|PM)\s+\w+\s+(\d{4})$/,
  );
  if (dateStrMatch) {
    const [, monthStr, day, hours, minutes, seconds, ampm, year] = dateStrMatch;
    const month = MONTH_NAMES.indexOf(monthStr);
    if (month !== -1) {
      let h = parseInt(hours, 10);
      if (ampm === 'PM' && h !== 12) h += 12;
      else if (ampm === 'AM' && h === 12) h = 0;
      return new Date(parseInt(year, 10), month, parseInt(day, 10), h, parseInt(minutes, 10), parseInt(seconds, 10));
    }
  }

  const timeOnlyMatch = datePart.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (timeOnlyMatch) {
    const [, hours, minutes, seconds] = timeOnlyMatch;
    const date = new Date();
    date.setHours(
      parseInt(hours, 10),
      parseInt(minutes, 10),
      parseInt(seconds ?? "0", 10),
      0,
    );
    return date;
  }

  const dmyMatch = datePart.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (dmyMatch) {
    const [, day, month, yearRaw, hours = "0", minutes = "0", seconds = "0"] =
      dmyMatch;
    const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;
    return new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hours, 10),
      parseInt(minutes, 10),
      parseInt(seconds, 10),
    );
  }

  return new Date(NaN);
};

const getHalfHourBucket = (date: Date) => {
  const bucketMs = 30 * 60 * 1000;
  return Math.floor(date.getTime() / bucketMs) * bucketMs;
};

const getBucketLabel = (timestamp: number) => {
  return new Date(timestamp).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const parseCreateCSVLogs = (
  logs: string,
): CreateCSVLogAnalysisResult => {
  const logEntries = logs.split("\n").map((line) => line.trimEnd());

  const runs: CreateCSVRun[] = [];
  let currentRun: CreateCSVRun | null = null;
  let insideError = false;

  for (const rawLine of logEntries) {
    const line = rawLine.trim();
    if (line === "") continue;

    const runHeaderMatch = line.match(/^=== Run at (.+) ===$/);
    if (runHeaderMatch) {
      if (currentRun) {
        runs.push(currentRun);
      }
      currentRun = {
        runAtText: runHeaderMatch[1],
        runAt: parseRunAt(runHeaderMatch[1]),
        algorithmDurations: [],
        errorLines: [],
        rawLines: [rawLine],
      };
      insideError = false;
      continue;
    }

    if (!currentRun) {
      continue;
    }

    currentRun.rawLines.push(rawLine);

    const algorithmMatch = line.match(/^Algorithm (.+?) Runtime (.+?) \|/);
    if (algorithmMatch) {
      currentRun.algorithmDurations.push({
        algorithm: algorithmMatch[1],
        durationText: algorithmMatch[2],
        durationMs: parseDurationText(algorithmMatch[2]),
      });
      insideError = false;
      continue;
    }

    const tracebackStart =
      line.startsWith("Traceback") ||
      line.startsWith("Exception") ||
      line.startsWith("KeyError") ||
      line.startsWith("TypeError") ||
      line.startsWith("ValueError");
    if (tracebackStart || insideError) {
      currentRun.errorLines.push(rawLine);
      insideError = true;
      continue;
    }
  }

  if (currentRun) {
    runs.push(currentRun);
  }

  const bucketMap = new Map<string, Map<string, CreateCSVAlgorithmDuration>>();
  const bucketOrder: string[] = [];

  runs.forEach((run, runIndex) => {
    const isValidDate = !Number.isNaN(run.runAt.getTime());
    const bucketKey = isValidDate
      ? String(getHalfHourBucket(run.runAt))
      : `invalid-${runIndex}-${run.runAtText}`;

    if (!bucketMap.has(bucketKey)) {
      bucketOrder.push(bucketKey);
      bucketMap.set(bucketKey, new Map<string, CreateCSVAlgorithmDuration>());
    }

    const bucket = bucketMap.get(bucketKey)!;

    run.algorithmDurations.forEach((duration) => {
      const existing = bucket.get(duration.algorithm);
      if (!existing || duration.durationMs > existing.durationMs) {
        bucket.set(duration.algorithm, duration);
      }
    });
  });

  const bucketedRuns = bucketOrder.map((bucketKey) => {
    const bucket = bucketMap.get(bucketKey)!;
    const bucketAt = Number.isNaN(Number(bucketKey))
      ? undefined
      : new Date(Number(bucketKey));

    return {
      bucketAt,
      bucketLabel: bucketAt?.getTime()
        ? getBucketLabel(bucketAt.getTime())
        : bucketKey.replace(/^invalid-\d+-/, ""),
      algorithmDurations: Array.from(bucket.values()),
    };
  });

  const totalAlgorithms = runs.reduce(
    (sum, run) => sum + run.algorithmDurations.length,
    0,
  );
  const totalErrors = runs.reduce((sum, run) => sum + run.errorLines.length, 0);

  return {
    runs,
    bucketedRuns,
    totalAlgorithms,
    totalErrors,
  };
};
